import { Api } from "@vzbl/integrations";
import { useMemo } from "react";
import { sortNatural } from "src/utils/sort";
import { unitTypeForCol, formatValue } from "src/utils/strings";
import { ExploreContext } from "../useExploreContext";
import { valueFromRecord } from "./common";
import { TimeUnit } from "./FormattingControls";

export function useChartData(
  aggregations: ExploreContext["aggregations"],
  {
    asPercent,
    hasCompare,
    hasPercentChange,
    invertGroups,
    nullToZero,
    pruneZeros,
    vertical,
    timeUnit,
  }: Partial<{
    asPercent: boolean;
    hasCompare: boolean;
    hasPercentChange: boolean;
    invertGroups: boolean;
    nullToZero: boolean;
    pruneZeros: boolean;
    vertical: boolean;
    timeUnit: TimeUnit;
  }> = {}
): {
  xKey: string;
  yKey?: string;
  yKeys: string[];
  xType: { type: string; unit?: string; tuple: boolean };
  yType: { type: string; unit?: string; tuple: boolean };
  zType: { type: string; unit?: string; tuple: boolean };
  xAxisType: any;
  yAxisType: any;
  data: Array<Record<string, string | number>>;
  labels: Array<Record<string, string | undefined>>;
} {
  // @ts-expect-error
  if (aggregations.data?.name === "ZodError")
    // @ts-expect-error
    throw new Error(JSON.stringify(aggregations.data?.issues, null, 3));

  const { data, meta } = aggregations.data || {};
  const columnInfo = useMemo(() => meta?.columnInfo || [], [meta?.columnInfo]);

  return useMemo(() => {
    const keys = Object.keys((data || [])[0] || {});
    if (invertGroups) {
      const aggKey = keys.pop();
      keys.reverse();
      keys.push(aggKey!);
    }
    const table: Record<
      string,
      Record<string, { value: string | number; label: string; record: any }>
    > = {};
    const xKey = keys.at(0)!;
    const yKey = keys.at(1);
    const yKeys: string[] = [];
    const zKey = keys.at(-1);
    if (keys.length === 3)
      (data || []).map((record) => {
        table[colVal(record, xKey)] = table[colVal(record, xKey)] || {
          [xKey]: { value: colVal(record, xKey), label: undefined, record },
        };
        table[colVal(record, xKey)][colVal(record, keys.at(-2)!)] = {
          value: colVal(record, keys.at(-1)!),
          label: "",
          record,
        };
        if (!yKeys.includes(`${colVal(record, keys.at(-2)!)}`)) {
          yKeys.push(`${colVal(record, keys.at(-2)!)}`);
        }
      });
    else {
      yKeys.push(keys.at(-1)!);
      (data || []).map((record) => {
        table[colVal(record, xKey)] = table[colVal(record, xKey)] || {
          [xKey]: { value: colVal(record, xKey), label: "", record },
        };
        table[colVal(record, xKey)][keys.at(-1)!] = {
          value: colVal(record, keys.at(-1)!),
          label: "",
          record,
        };
      });
    }
    Object.keys(table).forEach((key) => {
      if (asPercent) {
        const total = yKeys.reduce((sum, yKey) => {
          return sum + (Number(table[key][yKey]?.value) || 0);
        }, 0);

        if (total > 0) {
          yKeys.forEach((yKey) => {
            if (table[key][yKey]) {
              const value = Number(table[key][yKey].value) || 0;
              table[key][yKey].value = (value / total) * 100;
            }
          });
        }
      } else {
        yKeys.forEach((yKey) => {
          if (!pruneZeros)
            table[key][yKey] ||= { value: 0, label: "", record: {} };
          else if (table[key][yKey]?.value === 0) delete table[key][yKey];
        });
      }
    });
    const xVals = sortNatural(Object.keys(table));
    const xType = unitTypeForCol(xKey, columnInfo);

    const yType =
      yKey == zKey
        ? { type: "string", tuple: false }
        : unitTypeForCol(yKey!, columnInfo); // if yKey is same as zKey, bars are not broken down in groups.

    const zType = unitTypeForCol(zKey!, columnInfo);

    const yAxisType = vertical ? xType : zType;
    const xAxisType = vertical ? zType : xType;

    const [values, labels] = Object.values(table)
      .sort(
        (a, b) => xVals.indexOf(a[xKey].value) - xVals.indexOf(b[xKey].value)
      )
      .reduce(
        ([values_, labels_], _cur) => {
          const row = Object.fromEntries(
            Object.entries(_cur).map(([key, val]) => {
              return [key, val.value];
            })
          );

          if (asPercent) {
            const total = yKeys.reduce((sum, yKey) => {
              return sum + (Number(row[yKey]) || 0);
            }, 0);

            if (total > 0) {
              yKeys.forEach((yKey) => {
                if (row[yKey]) {
                  const value = Number(row[yKey]) || 0;
                  row[yKey] = (value / total) * 100;
                }
              });
            }
          }

          values_.push(row);

          labels_.push(
            Object.fromEntries(
              Object.entries(_cur).map(([key, val]) => {
                const value = valueFromRecord(
                  { [zKey!]: "0", ...val.record },
                  zKey!,
                  columnInfo
                );
                if (
                  hasCompare &&
                  value.delta !== undefined &&
                  value.percent !== undefined
                ) {
                  const label =
                    (hasPercentChange ? value.percent + "%" : value.delta) + "";
                  return [key, label.startsWith("-") ? label : "+" + label];
                }
                return [key, val.label];
              })
            )
          );
          return [values_, labels_];
        },
        [[], []] as [
          Array<Record<string, string | number>>,
          Array<Record<string, string | undefined>>
        ]
      );
    if (asPercent) {
      values.forEach((record, i) => {
        const total = Object.entries(record).reduce(
          (acc, [key, val]) => acc + (key === xKey ? 0 : Number(val)),
          0
        );
        const replacement = Object.fromEntries(
          Object.entries(record).map((entry) => {
            if (entry[0] === xKey) return entry;
            if (total === 0) return [entry[0], 0];
            return [entry[0], (Number(entry[1]) / total) * 100];
          })
        );
        values[i] = replacement;
      });
    }
    return {
      xKey,
      yKey,
      yKeys,
      data: values,
      labels,
      xType,
      yType,
      zType,
      xAxisType,
      yAxisType,
    };

    function colVal(
      record: Record<string, Api.FieldValue>,
      key: string
    ): string {
      const val = valueFromRecord(record, key, columnInfo);
      const unitType = unitTypeForCol(key, columnInfo);

      return val.value === "__VZBL::null__" &&
        nullToZero &&
        xKey !== key &&
        columnInfo.find((c) => c.key == key)?.type != "interval"
        ? "0"
        : val.value;
    }
  }, [
    asPercent,
    data,
    pruneZeros,
    invertGroups,
    columnInfo,
    hasCompare,
    hasPercentChange,
    nullToZero,
    vertical,
  ]);
}
